const CLIPR_ORIGIN = "https://app.clipr.tv";
export const CLIPR_WEBSITE_ORIGIN = "https://clipr.tv";
export const CLIPR_WEBSITE_LOCAL_ORIGIN = "http://localhost:4173";
const MOMO_ORIGIN = "https://momo-8rxu.onrender.com";
// Product decision: we currently run in Clipr-only mode.
export const CLIPR_ONLY_MODE = true;

function normalizeOrigin(value) {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `https://${value}`;
}

function getWindowOrigin() {
  if (typeof window === "undefined" || !window.location) return "";
  return window.location.origin;
}

function getWindowHostname() {
  if (typeof window === "undefined" || !window.location) return "";
  return window.location.hostname || "";
}

function isTunnelHost(hostname = getWindowHostname()) {
  const normalized = String(hostname || "").toLowerCase();
  return (
    normalized.endsWith(".ngrok-free.app") ||
    normalized.endsWith(".ngrok.app") ||
    normalized.endsWith(".ngrok.io")
  );
}

function isLocalDevHost(hostname = getWindowHostname()) {
  const normalized = String(hostname || "").toLowerCase();
  return (
    normalized === "localhost" ||
    normalized === "127.0.0.1" ||
    normalized === "::1" ||
    isTunnelHost(normalized)
  );
}

export function isCliprHostname(hostname = getWindowHostname()) {
  if (CLIPR_ONLY_MODE) return true;
  const normalized = String(hostname || "").toLowerCase();
  if (isLocalDevHost(normalized)) return true;
  return normalized === "clipr.tv" || normalized.endsWith(".clipr.tv");
}

export function getDomainContext(hostname = getWindowHostname()) {
  if (CLIPR_ONLY_MODE) return "clipr";
  return isCliprHostname(hostname) ? "clipr" : "momo";
}

export function getPreferredSiteOrigin(context = getDomainContext()) {
  const envSite = normalizeOrigin(process.env.VUE_APP_SITE_URL);
  const currentOrigin = normalizeOrigin(getWindowOrigin());
  const currentHostname = getWindowHostname();

  // In local development never force production domains.
  if (isLocalDevHost(currentHostname) && currentOrigin) {
    return currentOrigin;
  }

  if (CLIPR_ONLY_MODE || context === "clipr") {
    return CLIPR_ORIGIN;
  }

  // For Momo context, respect explicit env configuration when available.
  if (envSite) return envSite;
  return MOMO_ORIGIN;
}

/**
 * Get the correct redirect URL for OAuth based on the current domain context.
 * Must land on /login (public) so the auth guard does not bounce to clipr.tv
 * before Supabase processes the OAuth hash.
 */
export function getRedirectURL(
  context = getDomainContext(),
  redirectPath = "/create-clips"
) {
  const safePath = normalizeRedirectPath(redirectPath);
  const origin = (
    getPreferredSiteOrigin(context)
    || getWindowOrigin()
    || CLIPR_ORIGIN
  ).replace(/\/+$/, "");
  const loginUrl = new URL(`${origin}/login`);
  loginUrl.searchParams.set("redirect", safePath);
  return loginUrl.toString();
}

/**
 * Get the password recovery redirect URL for Supabase Auth emails.
 */
export function getPasswordRecoveryRedirectURL(context = getDomainContext()) {
  const origin = getPreferredSiteOrigin(context).replace(/\/+$/, "");
  return `${origin}/update-password`;
}

/**
 * Get current runtime origin (best effort fallback to preferred domain).
 */
export function getCurrentOrigin(context = getDomainContext()) {
  return getWindowOrigin() || getPreferredSiteOrigin(context);
}

function normalizeRedirectPath(redirectPath = "/create-clips") {
  const rawPath = String(redirectPath || "").trim();
  if (!rawPath.startsWith("/")) return "/create-clips";
  if (rawPath === "/login") return "/create-clips";
  return rawPath;
}

export function getWebsiteOrigin(options = {}) {
  const forceExternalOrigin = options?.forceExternalOrigin === true;
  const currentOrigin = normalizeOrigin(getWindowOrigin());
  const currentHostname = getWindowHostname();
  if (isLocalDevHost(currentHostname)) {
    if (forceExternalOrigin) {
      return CLIPR_WEBSITE_LOCAL_ORIGIN;
    }
    if (currentOrigin) {
      return currentOrigin;
    }
    return CLIPR_WEBSITE_LOCAL_ORIGIN;
  }
  return CLIPR_WEBSITE_ORIGIN;
}

export function getWebsiteLoginUrl(redirectPath = "/create-clips", options = {}) {
  const safeRedirectPath = normalizeRedirectPath(redirectPath);
  const websiteOrigin = getWebsiteOrigin(options).replace(/\/+$/, "");
  const url = new URL(`${websiteOrigin}/`);
  url.searchParams.set("redirect", safeRedirectPath);
  return url.toString();
}

export function getWebsiteHomeUrl(options = {}) {
  const websiteOrigin = getWebsiteOrigin(options).replace(/\/+$/, "");
  const locale = String(options.locale).toLowerCase();
  if (locale !== "es" && locale !== "en") {
    throw new Error("A supported locale is required for the website redirect.");
  }
  const pathname = locale === "en" ? "/en/" : "/";
  return `${websiteOrigin}${pathname}`;
}

export function redirectToWebsiteHome(options = {}) {
  if (typeof window === "undefined" || !window.location) return false;
  const destinationUrl = getWebsiteHomeUrl(options);
  const currentOrigin = normalizeOrigin(getWindowOrigin()).replace(/\/+$/, "");
  const destinationOrigin = normalizeOrigin(new URL(destinationUrl).origin).replace(/\/+$/, "");
  if (!destinationOrigin || !currentOrigin || destinationOrigin === currentOrigin) {
    return false;
  }
  window.location.replace(destinationUrl);
  return true;
}

export function redirectToWebsite(redirectPath = "/create-clips", options = {}) {
  if (typeof window === "undefined" || !window.location) return false;
  const destinationUrl = getWebsiteLoginUrl(redirectPath, options);
  const currentOrigin = normalizeOrigin(getWindowOrigin()).replace(/\/+$/, "");
  const destinationOrigin = normalizeOrigin(new URL(destinationUrl).origin).replace(/\/+$/, "");
  if (!destinationOrigin || !currentOrigin || destinationOrigin === currentOrigin) {
    return false;
  }
  window.location.replace(destinationUrl);
  return true;
}

export function leaveAppForWebsite(options = {}) {
  const mergedOptions = { forceExternalOrigin: true, ...options };
  if (redirectToWebsiteHome(mergedOptions)) {
    return true;
  }
  window.location.replace(getWebsiteHomeUrl(mergedOptions));
  return true;
}

